import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { X } from '@phosphor-icons/react';
import AdvanceDataTable from '@/components/core/advance-data-table/advance-data-table';
import { LinkedRecordBar, PageHeader } from '@/components/core';
import { Button } from '@/components/ui-kit/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui-kit/tabs';
import { HRM_PERMISSIONS } from '@/constant/hr/permissions';
import { useHasPermission } from '@/hooks/use-has-permission';
import { useLinkedRecord, useOpenLinkedRecord } from '@/hooks/use-linked-record';
import { useServerTable } from '@/hooks/use-server-table';
import { mergeWhere } from '@/lib/table-query';
import { useAuthStore } from '@/state/store/auth';
import { useDepartmentScope, useEmployeesByUserIds } from '@/modules/shared/hr-lookups';
import { createLeaveRequestsTableColumns } from '../../components/leave-requests-table/leave-requests-table-columns';
import { LeaveReviewDialog } from '../../components/leave-review-dialog/leave-review-dialog';
import { LeaveSheetTab } from '../../components/leave-sheet-tab/leave-sheet-tab';
import { LeaveOverviewTab } from '../../components/leave-overview-tab/leave-overview-tab';
import { useBulkReviewLeaveRequests, useGetLeaveRequests } from '../../hooks/use-leave-manager';
import { DEFAULT_LEAVE_REQUESTS_ORDER } from '../../graphql/queries';
import {
  LeaveRequest,
  LeaveRequestsPagingResult,
  LeaveStatus,
} from '../../types/leave-manager.types';

const RequestsTab = ({ isArchived }: { isArchived: boolean }) => {
  const { t } = useTranslation();
  const canReview = useHasPermission(HRM_PERMISSIONS.LEAVE.REVIEW);
  const currentUserId = useAuthStore((state) => state.user?.itemId ?? '');

  // A notification's link lists only its request and opens it.
  const { linkedId, clearLinked } = useLinkedRecord();
  const { pagination, order, columnWhere, tableProps } = useServerTable({
    pageSize: 15,
    defaultOrder: DEFAULT_LEAVE_REQUESTS_ORDER,
    linkedId,
  });
  const [reviewTarget, setReviewTarget] = useState<LeaveRequest | null>(null);

  const {
    departments,
    getDepartmentIdCondition,
    isLoading: isScopeLoading,
  } = useDepartmentScope();

  // The department scope (the Department permissions, as the TOIL Manager) always applies, so a
  // reviewer limited to some departments never sees requests outside them; the Department column's
  // filter only narrows within it (mergeWhere). The leave workflows still check who may act.
  const departmentCondition = getDepartmentIdCondition('');
  const filter = {
    IsArchived: isArchived,
    ...(departmentCondition ? { DepartmentId: departmentCondition } : {}),
  };

  const { data, isLoading, error } = useGetLeaveRequests({
    pageNo: pagination.pageIndex + 1,
    pageSize: pagination.pageSize,
    filter: mergeWhere(filter, columnWhere),
    order,
  });
  const result = (data as { LeaveRequests?: LeaveRequestsPagingResult })?.LeaveRequests;
  const items = result?.items ?? [];
  const totalCount = result?.totalCount ?? 0;
  useOpenLinkedRecord(linkedId, items, setReviewTarget);

  const { byUserId } = useEmployeesByUserIds(items.map((item) => item.UserId));
  const emailOf = (userId: string) => byUserId.get(userId)?.Email;

  // No action column: clicking a row (a card on a phone) opens the request's details.
  const columns = createLeaveRequestsTableColumns({
    t,
    selectable: canReview,
    emailOf,
    departments,
  });

  // Bulk approve/reject still runs the per-item LEAVE_REQUEST_REVIEW workflow call (useBulkReviewLeaveRequests
  // → bulkReviewLeaveRequests → the same reviewLeaveRequest the single-row dialog uses), so every item is
  // independently re-checked server-side (permission, self-review block, Pending status) — no new write path.
  const {
    mutateAsync: bulkReview,
    isPending: isBulkReviewing,
    variables: bulkReviewVariables,
  } = useBulkReviewLeaveRequests();
  // Only the clicked button spins; the others are just disabled while it runs.
  const bulkReviewingStatus = isBulkReviewing ? bulkReviewVariables?.status : undefined;

  // A row can be bulk-selected only when it's Pending and isn't the signed-in user's own request — the
  // same client-side mirror of the workflow's self-review block LeaveReviewDialog uses, applied up front
  // so the row's checkbox is disabled instead of letting it be picked and then refused one by one.
  const canSelectRow = useCallback(
    (row: LeaveRequest) =>
      canReview && row.Status === 'Pending' && (!currentUserId || row.UserId !== currentUserId),
    [canReview, currentUserId]
  );

  return (
    <div className="flex flex-col gap-4">
      <LinkedRecordBar linkedId={linkedId} onShowAll={clearLinked} />
      <AdvanceDataTable
        {...tableProps}
        data={items}
        columns={columns}
        getRowId={(row) => row.ItemId}
        canSelectRow={canSelectRow}
        clearSelectionOnPageChange
        // Until the departments load, a scoped user's scope matches nothing; show the loader, not an
        // empty list.
        isLoading={isLoading || isScopeLoading}
        error={error instanceof Error ? error : null}
        pagination={{
          pageIndex: pagination.pageIndex,
          pageSize: pagination.pageSize,
          totalCount,
        }}
        isExpandRowContent={false}
        onRowClick={setReviewTarget}
        mobileView="cards"
        columnsToolbar={(table) => {
          const selectedRows = table.getSelectedRowModel().rows;
          if (!canReview || selectedRows.length === 0) return null;
          const itemIds = selectedRows.map((row) => row.original.ItemId);

          const runBulkReview = async (status: Extract<LeaveStatus, 'Approved' | 'Rejected'>) => {
            await bulkReview({ itemIds, status });
            table.resetRowSelection();
          };

          return (
            // As the attendance corrections' bar: on a phone the count and the X share the first
            // line, the buttons go under them.
            <div className="flex flex-wrap items-center gap-2 rounded-lg border bg-primary-50 px-3 py-2">
              <span className="min-w-0 flex-1 text-sm font-medium">
                {t('EMPLOYEES_SELECTED_COUNT', { count: itemIds.length })}
              </span>
              <div className="order-3 flex basis-full items-center justify-end gap-2 md:order-2 md:basis-auto">
                <Button
                  type="button"
                  size="sm"
                  loading={bulkReviewingStatus === 'Approved'}
                  disabled={isBulkReviewing}
                  onClick={() => runBulkReview('Approved')}
                >
                  {t('APPROVE')}
                </Button>
                <Button
                  type="button"
                  size="sm"
                  variant="destructive"
                  loading={bulkReviewingStatus === 'Rejected'}
                  disabled={isBulkReviewing}
                  onClick={() => runBulkReview('Rejected')}
                >
                  {t('REJECT')}
                </Button>
              </div>
              <Button
                type="button"
                size="icon"
                variant="ghost"
                className="order-2 size-8 shrink-0 md:order-3"
                aria-label={t('CLEAR_SELECTION')}
                disabled={isBulkReviewing}
                onClick={() => table.resetRowSelection()}
              >
                <X className="size-4" />
              </Button>
            </div>
          );
        }}
      />

      <LeaveReviewDialog
        open={Boolean(reviewTarget)}
        onOpenChange={(open) => !open && setReviewTarget(null)}
        leaveRequest={reviewTarget}
      />
    </div>
  );
};

export const LeaveManagerPage = () => {
  const { t } = useTranslation();
  // The route already gates the whole page on this permission; gating the two read-only tabs too is
  // defense in depth, not the only check.
  const canViewAll = useHasPermission(HRM_PERMISSIONS.LEAVE.VIEW_ALL);

  return (
    <div className="flex w-full flex-col gap-4">
      <PageHeader title={t('LEAVE_MANAGER')} />

      <Tabs defaultValue="all">
        {/* Full width on a phone, each tab an equal share (scrolls if they don't fit). */}
        <TabsList className="max-sm:w-full max-sm:justify-start max-sm:overflow-x-auto">
          <TabsTrigger value="all" className="max-sm:flex-1">
            {t('ALL_REQUESTS')}
          </TabsTrigger>
          <TabsTrigger value="archived" className="max-sm:flex-1">
            {t('ARCHIVED')}
          </TabsTrigger>
          {canViewAll && (
            <TabsTrigger value="leave-sheet" className="max-sm:flex-1">
              {t('LEAVE_SHEET')}
            </TabsTrigger>
          )}
          {canViewAll && (
            <TabsTrigger value="leave-overview" className="max-sm:flex-1">
              {t('LEAVE_OVERVIEW')}
            </TabsTrigger>
          )}
        </TabsList>
        <TabsContent value="all">
          <RequestsTab isArchived={false} />
        </TabsContent>
        <TabsContent value="archived">
          <RequestsTab isArchived />
        </TabsContent>
        {canViewAll && (
          <TabsContent value="leave-sheet">
            <LeaveSheetTab />
          </TabsContent>
        )}
        {canViewAll && (
          <TabsContent value="leave-overview">
            <LeaveOverviewTab />
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
};
